import { z } from 'zod';

// What the integration health screen shows (DESIGN.md §7: every external call has a
// visible state — waiting, success, failed with reason, retrying; no silent failures).
// These views carry IDs, codes and counts only, never a payload.

export const OUTBOX_STATUSES = ['pending', 'retrying', 'sent', 'dead'] as const;

export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export const outboxEventSchema = z.object({
  id: z.uuid(),
  /** e.g. mpesa.stk_push, etims.sign_receipt, sha.claim_submit */
  topic: z.string(),
  aggregateType: z.string().nullable(),
  aggregateId: z.uuid().nullable(),
  /** The key sent to the external system, so support can match a call on their side. */
  idempotencyKey: z.string(),
  status: z.enum(OUTBOX_STATUSES),
  attempts: z.number().int(),
  lastErrorCode: z.string().nullable(),
  lastAttemptAt: z.iso.datetime().nullable(),
  sentAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});

export type OutboxEventView = z.infer<typeof outboxEventSchema>;

export const integrationMessageSchema = z.object({
  id: z.uuid(),
  occurredAt: z.iso.datetime(),
  outboxEventId: z.uuid().nullable(),
  /** sha_hie, etims, mpesa, sms, whatsapp, system */
  system: z.string(),
  direction: z.enum(['outbound', 'inbound']),
  /** A path template such as /claims/{id}, never a URL with a query string. */
  endpoint: z.string(),
  correlationId: z.string().nullable(),
  outcome: z.enum(['succeeded', 'failed']),
  httpStatus: z.number().int().nullable(),
  durationMs: z.number().int(),
  errorCode: z.string().nullable(),
});

export type IntegrationMessageView = z.infer<typeof integrationMessageSchema>;

export const integrationHealthSchema = z.object({
  counts: z.object({
    pending: z.number().int(),
    retrying: z.number().int(),
    sent: z.number().int(),
    dead: z.number().int(),
  }),
  /** When the oldest event still waiting or retrying was created. */
  oldestWaitingAt: z.iso.datetime().nullable(),
  lastMessageAt: z.iso.datetime().nullable(),
});

export type IntegrationHealthView = z.infer<typeof integrationHealthSchema>;
